import { useState, useEffect } from 'react';
import { getCurrentUser, getCreditos } from '../services/usuario';
import { obtenerPlanes, iniciarPago } from '../services/pagos';
import { listarCampanias } from '../services/campanias';
import '../styles/Dashboard.css';

export default function Dashboard() {
  const [usuario, setUsuario] = useState(null);
  const [creditos, setCreditos] = useState(null);
  const [planes, setPlanes] = useState([]);
  const [totalCampanias, setTotalCampanias] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [comprando, setComprando] = useState(false);

  useEffect(() => {
    const cargarDatos = async () => {
      setLoading(true);
      setError('');
      try {
        const [userData, creditosData, planesData, campaniasData] = await Promise.all([
          getCurrentUser(),
          getCreditos(),
          obtenerPlanes(),
          listarCampanias()
        ]);
        setUsuario(userData);
        setCreditos(creditosData);
        setPlanes(planesData.planes || []);
        setTotalCampanias(campaniasData.length || 0);
      } catch (err) {
        console.error('Error cargando dashboard:', err);
        setError('Error al cargar los datos. Recarga la página.');
      } finally {
        setLoading(false);
      }
    };

    cargarDatos();
  }, []);

  const handleComprarPlan = async (planId) => {
    setComprando(true);
    setError('');
    try {
      const resultado = await iniciarPago(planId);
      if (resultado.redirect_url) {
        window.location.href = resultado.redirect_url;
      } else {
        setError('No se recibió URL de redirección de PayPhone');
      }
    } catch (err) {
      console.error('Error al comprar plan:', err);
      setError(err.message || 'Error al iniciar el pago');
    } finally {
      setComprando(false);
    }
  };

  if (loading) {
    return (
      <div className="dashboard-loading">
        <div className="spinner"></div>
        <p>Cargando dashboard...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-error">
        <p>{error}</p>
        <button onClick={() => window.location.reload()}>Reintentar</button>
      </div>
    );
  }

  const creditosDisponibles = creditos?.creditos_prueba || 0;
  const tieneCreditos = creditosDisponibles > 0 || creditos?.plan !== 'gratis';

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <h1>Bienvenido, {usuario?.nombre || 'Usuario'} 👋</h1>
        <p>Gestiona tus campañas de marketing con inteligencia artificial</p>
      </div>

      <div className="dashboard-cards">
        {/* Tarjeta de créditos */}
        <div className="card creditos-card">
          <div className="card-row">
            <div className="card-icon">🎨</div>
            <div className="card-info">
              <h3>Créditos disponibles</h3>
              <div className="creditos-valor">{creditosDisponibles}</div>
              <p className="creditos-plan">Plan: {creditos?.plan || 'gratis'}</p>
              {creditos?.fecha_expiracion_plan && (
                <p className="creditos-expiracion">
                  Expira: {new Date(creditos.fecha_expiracion_plan).toLocaleDateString()}
                </p>
              )}
            </div>
          </div>
          {!tieneCreditos && (
            <button className="btn-upgrade">Actualizar plan</button>
          )}
        </div>

        {/* Tarjeta de campañas */}
        <div className="card campanias-card">
          <div className="card-row">
            <div className="card-icon">📊</div>
            <div className="card-info">
              <h3>Total de campañas</h3>
              <div className="campanias-valor">{totalCampanias}</div>
              <p className="campanias-subtext">Campañas creadas</p>
            </div>
          </div>
        </div>
      </div>

      {/* SECCIÓN DE PLANES DE PAGO */}
      <div className="planes-section">
        <h2 className="planes-titulo">💳 Compra créditos adicionales</h2>
        <p className="planes-subtitulo">
          Elige el plan que mejor se adapte a tus necesidades. Cada crédito te permite generar una imagen o procesar un PDF.
        </p>
        
        <div className="planes-grid">
          {planes.map((plan) => (
            <div key={plan.id} className="plan-card">
              <div className="plan-header">
                <h3 className="plan-nombre">{plan.nombre}</h3>
                <div className="plan-precio">
                  <span className="plan-monto">${plan.precio.toFixed(2)}</span>
                  <span className="plan-moneda">USD</span>
                </div>
              </div>
              <div className="plan-cuerpo">
                <div className="plan-creditos">
                  <span className="plan-creditos-numero">{plan.creditos}</span>
                  <span className="plan-creditos-label">créditos</span>
                </div>
                <p className="plan-descripcion">
                  {plan.creditos === 60 && 'Ideal para empezar y probar la plataforma.'}
                  {plan.creditos === 120 && 'Perfecto para pequeños negocios y creadores.'}
                  {plan.creditos === 200 && 'Para profesionales y agencias con alto volumen.'}
                </p>
                <button 
                  className="plan-btn-comprar" 
                  onClick={() => handleComprarPlan(plan.id)}
                  disabled={comprando}
                >
                  {comprando ? 'Procesando...' : `Comprar por $${plan.precio.toFixed(2)}`}
                </button>
              </div>
            </div>
          ))}
        </div>
        {error && <div className="plan-error">{error}</div>}
      </div>

      <div className="dashboard-tips">
        <h3>💡 ¿Cómo funcionan los créditos?</h3>
        <ul>
          <li><strong>1 crédito</strong> = Generar una imagen desde un PDF (sin referencias)</li>
          <li><strong>3 créditos</strong> = Generar una imagen con referencias visuales (logo, publicidad, producto)</li>
          <li><strong>1 crédito</strong> = Procesar un nuevo PDF y generar sus chunks</li>
          <li>Los créditos <strong>no caducan</strong> y se acumulan con cada compra</li>
          <li>Puedes comprar créditos en cualquier momento desde este panel</li>
        </ul>
      </div>
    </div>
  );
}